import { useEffect, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import HubSpotVideoPlayer from 'ui-addon-video-player/components/HubSpotVideoPlayer';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { CalloutButton } from './CalloutButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function CalloutHero({
  title,
  description,
  buttonText,
  buttonUrl,
  buttonUse = 'secondary',
  secondaryButtonText,
  secondaryButtonUrl,
  secondaryButtonUse = 'secondary',
  imageUrl,
  videoId,
  videoInteractive = false,
  onClick,
  openCopilot,
  CtaButton = CalloutButton
}) {
  const {
    isMobile
  } = useResponsiveProps({
    isMobile: {
      xs: true,
      md: false
    }
  });
  // A video always wins when present; if the player reports an error we fall
  // back to the still image (when one was provided). Reset the failed flag when
  // the video changes so a prior failure doesn't stick to a new/corrected ID.
  const [videoFailed, setVideoFailed] = useState(false);
  useEffect(() => setVideoFailed(false), [videoId]);
  const hasSecondaryButton = Boolean(secondaryButtonText && secondaryButtonUrl);
  const showVideo = videoId != null && !videoFailed;
  const hasMedia = showVideo || Boolean(imageUrl);
  const textContent = /*#__PURE__*/_jsxs(Flex, {
    alignItems: "start",
    flexDirection: "column",
    gap: "600",
    justifyContent: "center",
    flex: isMobile || !hasMedia ? undefined : '1 1 50%',
    children: [/*#__PURE__*/_jsxs(View, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        marginBlockEnd: "400",
        typography: "heading-400",
        children: title
      }), /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "body-200",
        children: description
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "stretch",
      gap: "400",
      flexWrap: "wrap",
      children: [/*#__PURE__*/_jsx(CtaButton, {
        buttonText: buttonText,
        buttonUrl: buttonUrl,
        onClick: onClick,
        openCopilot: openCopilot,
        use: buttonUse
      }), hasSecondaryButton && /*#__PURE__*/_jsx(CtaButton, {
        buttonText: secondaryButtonText,
        buttonUrl: secondaryButtonUrl,
        use: secondaryButtonUse
      })]
    })]
  });

  // `HubSpotVideoPlayer` needs a definite-height box as a direct child. Center
  // that box in the media column (like images) so a taller sibling card doesn't
  // pin the player to the top. `contain` matches the image treatment so frames
  // aren't cropped edge-to-edge against the card padding.
  const videoContent = /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    justifyContent: "center",
    flex: isMobile ? undefined : '1 1 50%',
    alignSelf: isMobile ? undefined : 'stretch',
    overflow: "hidden",
    borderRadius: "300",
    paddingInline: isMobile ? undefined : '400',
    pointerEvents: videoInteractive ? undefined : 'none',
    children: /*#__PURE__*/_jsx(Flex, {
      blockSize: "260px",
      inlineSize: "100%",
      maxInlineSize: "440px",
      overflow: "hidden",
      borderRadius: "300",
      children: /*#__PURE__*/_jsx(HubSpotVideoPlayer, {
        videoId: videoId,
        autoPlay: false,
        mutedByDefault: true,
        objectFit: "contain",
        onError: () => setVideoFailed(true)
      })
    })
  });
  const imageContentInner = imageUrl ? /*#__PURE__*/_jsx(Flex, {
    alignItems: "center",
    justifyContent: "center",
    flex: isMobile ? undefined : '1 1 50%',
    maxInlineSize: isMobile ? undefined : '50%',
    blockSize: isMobile ? '240px' : '260px',
    alignSelf: isMobile ? undefined : 'center',
    minInlineSize: "0",
    overflow: "hidden",
    borderRadius: "300",
    children: /*#__PURE__*/_jsx(Image, {
      alt: "",
      blockSize: "100%",
      borderRadius: "300",
      inlineSize: "100%",
      loading: "lazy",
      objectFit: "cover",
      src: imageUrl
    })
  }) : null;
  const mediaContent = showVideo ? videoContent : imageContentInner;

  // Fill the row height so bottoms align with a sibling CalloutCard.
  return /*#__PURE__*/_jsx(CardWrapper, {
    margin: "0",
    overflow: "hidden",
    blockSize: "100%",
    flex: "1",
    children: /*#__PURE__*/_jsx(CardSection, {
      padding: "0",
      margin: "0",
      blockSize: "100%",
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: isMobile ? 'stretch' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: "600",
        paddingBlock: showVideo ? '600' : '400',
        paddingInlineStart: "800",
        paddingInlineEnd: isMobile || showVideo ? '800' : '200',
        blockSize: "100%",
        children: isMobile ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [mediaContent, textContent]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [textContent, mediaContent]
        })
      })
    })
  });
}